import { useCallback, useRef, useState, useEffect } from "react";
import {
    ExtendedMarkerData,
    CircleData,
    MarkerModalData,
    MapInstance,
    MapGL,
    CitySettings,
} from "@/types/map";
import { createMarkerIcon, MARKER_SIZES, MARKER_ANCHORS } from "../../utils/map/mapIcons";
import { CIRCLE_COLORS, CIRCLE_CONFIG } from "../../utils/map/mapStyles";
import { getMarkerTypeByEvents, groupEventsByLocation, centerMapOnMarker } from "../../utils/map//mapUtils";
import { calculateMarkerOpacity } from "../../utils/timeUtils";
import { HapticFeedback } from "../../utils/hapticFeedback";
import { YandexMetrika } from "../../utils/yandexMetrika";
import { createManagedMarker } from "./markerFactory";
import { useAppSelector } from "../redux";
import {
    selectIsDpsMarkersVisible,
    selectIsWebcamsMarkersVisible,
    selectIsCleanMarkersVisible,
    selectIsDtpMarkersVisible,
} from "../../store/selectors/mapSelectors";

export const useMarkerManager = (
    map: MapInstance | null,
    mapgl: MapGL | null,
    isMapReady: boolean,
    theme: "light" | "dark",
    onCloseFilters?: () => void
) => {
    // Используем мемоизированные селекторы для предотвращения лишних ре-рендеров
    const isDpsMarkersVisible = useAppSelector(selectIsDpsMarkersVisible);
    const isWebcamsMarkersVisible = useAppSelector(selectIsWebcamsMarkersVisible);
    const isCleanMarkersVisible = useAppSelector(selectIsCleanMarkersVisible);
    const isDtpMarkersVisible = useAppSelector(selectIsDtpMarkersVisible);
    const simplifiedMapRendering = useAppSelector((state) => state.settings.simplifiedMapRendering);
    const markerRenderingMode = simplifiedMapRendering ? "html" : "webgl";
    const [selectedMarkerId, setSelectedMarkerId] = useState<string | null>(null);
    const [markerModal, setMarkerModal] = useState<MarkerModalData>({
        isOpen: false,
        data: null,
    });

    const currentMarkers = useRef<ExtendedMarkerData[]>([]);
    const currentCircles = useRef<CircleData[]>([]);
    const markersAddedRef = useRef(false);
    const currentAnimationRef = useRef<null | (() => void)>(null);
    const initialSyncDone = useRef(false);

    // Индекс маркеров по типам для быстрого доступа (оптимизация производительности)
    const markersByType = useRef<{
        dps: Set<number>;
        webcam: Set<number>;
        clean: Set<number>;
        dtp: Set<number>;
    }>({
        dps: new Set(),
        webcam: new Set(),
        clean: new Set(),
        dtp: new Set(),
    });

    // Проверка видимости маркера по типу (без учета времени)
    const isMarkerVisibleByFilter = useCallback((markerType: string, type: "event" | "webcam"): boolean => {
        if (type === "webcam") return isWebcamsMarkersVisible;
        if (type === "event") {
            if (markerType === "dps") return isDpsMarkersVisible;
            if (markerType === "clean") return isCleanMarkersVisible;
            if (markerType === "dtp") return isDtpMarkersVisible;
        }
        return true;
    }, [isDpsMarkersVisible, isWebcamsMarkersVisible, isCleanMarkersVisible, isDtpMarkersVisible]);

    // Функция для вычисления итоговой прозрачности (фильтры + время)
    const calculateFinalOpacity = useCallback((
        markerType: string,
        type: "event" | "webcam",
        timestamp?: number
    ): number => {
        // Проверяем фильтр
        if (!isMarkerVisibleByFilter(markerType, type)) return 0;

        // Для событий учитываем время
        if (type === "event" && typeof timestamp === "number") {
            return calculateMarkerOpacity(timestamp);
        }

        return 1.0;
    }, [isMarkerVisibleByFilter]);

    // Оптимизированная функция обновления маркеров по типам (использует индекс)
    const batchUpdateMarkersByTypes = useCallback((
        filterUpdates: Array<{
            filterType: "dps" | "webcam" | "clean" | "dtp";
            isVisible: boolean;
        }>
    ) => {
        if (!isMapReady || currentMarkers.current.length === 0) return;

        // Собираем все обновления за один проход
        const updates: Array<{
            marker: any;
            currentOpacity: number;
            targetOpacity: number;
            iconConfig: any;
        }> = [];

        // Используем индекс для быстрого доступа к нужным маркерам
        filterUpdates.forEach(({ filterType, isVisible }) => {
            const indices = markersByType.current[filterType];

            indices.forEach((index) => {
                if (index >= currentMarkers.current.length) return;

                const { marker, type, timestamp } = currentMarkers.current[index];
                const userData = marker.userData as any;
                if (!userData) return;

                const markerType = userData.markerType;

                // Вычисляем базовую прозрачность по времени
                let baseOpacity = 1.0;
                if (type === "event" && typeof timestamp === "number") {
                    baseOpacity = calculateMarkerOpacity(timestamp);
                }

                const targetOpacity = isVisible ? baseOpacity : 0;

                // Правильно получаем текущую прозрачность из userData или используем targetOpacity
                const currentOpacity = (userData as any).currentOpacity ?? targetOpacity;

                updates.push({
                    marker,
                    currentOpacity,
                    targetOpacity,
                    iconConfig: {
                        icon: createMarkerIcon(markerType, userData.locationType, theme),
                        size: MARKER_SIZES.normal,
                        anchor: MARKER_ANCHORS.normal,
                    }
                });
            });
        });

        // Плавная анимация с использованием requestAnimationFrame
        if (updates.length > 0) {
            const duration = 300; // 300мс анимация
            const startTime = performance.now();

            const animate = (currentTime: number) => {
                const elapsed = currentTime - startTime;
                const progress = Math.min(elapsed / duration, 1);

                // Easing function для плавности (easeInOutCubic)
                const eased = progress < 0.5
                    ? 4 * progress * progress * progress
                    : 1 - Math.pow(-2 * progress + 2, 3) / 2;

                updates.forEach(({ marker, currentOpacity, targetOpacity, iconConfig }) => {
                    try {
                        const opacity = currentOpacity + (targetOpacity - currentOpacity) * eased;
                        marker.setIcon({
                            ...iconConfig,
                            opacity: opacity,
                        });

                        // Сохраняем текущую прозрачность в userData для плавности следующей анимации
                        if (marker.userData) {
                            (marker.userData as any).currentOpacity = opacity;
                        }
                    } catch (error) {
                        console.warn("Ошибка анимации маркера:", error);
                    }
                });

                if (progress < 1) {
                    requestAnimationFrame(animate);
                } else {
                    const types = filterUpdates.map(u => u.filterType).join(', ');
                    console.log(`⚡ Анимировано ${updates.length} маркеров типов: ${types}`);
                }
            };

            requestAnimationFrame(animate);
        }
    }, [isMapReady, theme]);

    // Объединенный useEffect для всех фильтров (оптимизация производительности)
    useEffect(() => {
        if (!isMapReady || currentMarkers.current.length === 0) return;

        // Первичная синхронизация
        if (!initialSyncDone.current) {
            console.log("🔄 Первичная синхронизация видимости маркеров", {
                dps: isDpsMarkersVisible,
                webcams: isWebcamsMarkersVisible,
                clean: isCleanMarkersVisible,
                dtp: isDtpMarkersVisible,
            });
            initialSyncDone.current = true;
        }

        // Объединяем все обновления фильтров в один батч
        requestAnimationFrame(() => {
            batchUpdateMarkersByTypes([
                { filterType: "dps", isVisible: isDpsMarkersVisible },
                { filterType: "webcam", isVisible: isWebcamsMarkersVisible },
                { filterType: "clean", isVisible: isCleanMarkersVisible },
                { filterType: "dtp", isVisible: isDtpMarkersVisible },
            ]);
        });
    }, [isMapReady, isDpsMarkersVisible, isWebcamsMarkersVisible, isCleanMarkersVisible, isDtpMarkersVisible, batchUpdateMarkersByTypes]);

    const animateMarkerJump = useCallback((marker: any, baseCoords: [number, number]): (() => void) => {
        if (!map) return () => { };

        let animationId: number | null = null;
        const startTime = performance.now();
        const duration = 1400; // 0.8 секунды
        const amplitude = 8; // Маленькая высота

        function frame(currentTime: number) {
            const elapsed = (currentTime - startTime) % duration;
            const progress = elapsed / duration;

            const bounce = Math.sin(progress * Math.PI * 2) * amplitude;

            try {
                if (!map) return;
                const basePixels = map.project(baseCoords);
                const newPixels: [number, number] = [
                    basePixels[0],
                    basePixels[1] - Math.abs(bounce)
                ];
                const newCoords = map.unproject(newPixels);

                marker.setCoordinates(newCoords);
                animationId = requestAnimationFrame(frame);
            } catch (error) {
                console.warn("Ошибка анимации маркера:", error);
            }
        }

        animationId = requestAnimationFrame(frame);

        return function stopAnimation() {
            if (animationId) {
                cancelAnimationFrame(animationId);
                animationId = null;
                try {
                    marker.setCoordinates(baseCoords);
                } catch (error) {
                    console.warn("Ошибка при остановке анимации:", error);
                }
            }
        };
    }, [map]);

    const clearAllMarkers = useCallback(() => {

        if (currentAnimationRef.current) {
            currentAnimationRef.current();
            currentAnimationRef.current = null;
        }

        currentMarkers.current.forEach(({ marker }) => {
            try {
                marker.destroy();
            } catch (error) {
                console.warn("Ошибка при удалении маркера:", error);
            }
        });
        currentMarkers.current = [];

        // Очищаем индекс маркеров
        markersByType.current = {
            dps: new Set(),
            webcam: new Set(),
            clean: new Set(),
            dtp: new Set(),
        };

        currentCircles.current.forEach(({ circle }) => {
            try {
                circle.destroy();
            } catch (error) {
                console.warn("Ошибка при удалении окружности:", error);
            }
        });
        currentCircles.current = [];

        markersAddedRef.current = false;
        initialSyncDone.current = false; // Сбрасываем флаг для следующей загрузки маркеров
    }, []);

    const resetAllMarkersSelection = useCallback(() => {

        if (currentAnimationRef.current) {
            currentAnimationRef.current();
            currentAnimationRef.current = null;
        }

        currentMarkers.current.forEach(({ marker }) => {
            try {
                const userData = marker.userData as any;
                if (userData) {
                    // Используем сохраненную прозрачность, чтобы не показывать скрытые метки
                    const savedOpacity = userData.currentOpacity ?? 1.0;

                    marker.setIcon({
                        icon: createMarkerIcon(userData.markerType, userData.locationType, theme),
                        size: MARKER_SIZES.normal,
                        anchor: MARKER_ANCHORS.normal,
                        opacity: savedOpacity, // Сохраняем текущую прозрачность
                    });
                }
            } catch (error) {
                console.warn("Ошибка при сбросе выделения маркера:", error);
            }
        });

        currentCircles.current.forEach(({ circle }) => {
            try {
                circle.destroy();
            } catch (error) {
                console.warn("Ошибка при удалении окружности:", error);
            }
        });
        currentCircles.current = [];
    }, [theme]);

    const createCircleForSelectedMarker = useCallback(
        (coordinates: [number, number], markerType: "dps" | "clean" | "dtp", markerId: string) => {
            if (!map || !mapgl) return;

            const existingCircle = currentCircles.current.find((c) => c.markerId === markerId);
            if (existingCircle) {
                console.log("Окружность уже существует для маркера:", markerId);
                return;
            }

            try {
                const circle = new mapgl.CircleMarker(map, {
                    coordinates: coordinates,
                    radius: CIRCLE_CONFIG.radius,
                    color: CIRCLE_COLORS[markerType],
                    strokeColor: CIRCLE_COLORS[markerType].replace("0.15", "0.6"),
                    strokeWidth: CIRCLE_CONFIG.strokeWidth,
                    interactive: CIRCLE_CONFIG.interactive,
                });

                currentCircles.current.push({
                    circle,
                    markerId,
                    type: markerType,
                });

                console.log(`✅ Создана окружность для ${markerType} маркера:`, markerId);
            } catch (error) {
                console.warn("Ошибка создания окружности:", error);
            }
        },
        [map, mapgl]
    );

    const selectMarker = useCallback(
        (markerId: string) => {
            console.log("🎯 Выделяем маркер:", markerId);

            resetAllMarkersSelection();

            const selectedMarker = currentMarkers.current.find(
                ({ marker }) => (marker.userData as any)?.markerId === markerId
            );

            if (selectedMarker) {
                try {
                    const userData = selectedMarker.marker.userData as any;

                    // Вычисляем финальную прозрачность с учетом фильтров И времени
                    const finalOpacity = calculateFinalOpacity(
                        userData.markerType, 
                        selectedMarker.type, 
                        selectedMarker.timestamp
                    );

                    // Увеличиваем размер маркера с сохранением прозрачности
                    selectedMarker.marker.setIcon({
                        icon: createMarkerIcon(userData.markerType, userData.locationType, theme),
                        size: MARKER_SIZES.selected,
                        anchor: MARKER_ANCHORS.selected,
                        opacity: finalOpacity, // Прозрачность с учетом фильтров
                    });
                    
                    // Обновляем currentOpacity
                    userData.currentOpacity = finalOpacity;

                    // Окружность не нужна - иконки уже различаются по типу

                    currentAnimationRef.current = animateMarkerJump(
                        selectedMarker.marker,
                        userData.coordinates
                    );
                    setSelectedMarkerId(markerId);
                } catch (error) {
                    console.warn("Ошибка при выделении маркера:", error);
                }
            }
        },
        [resetAllMarkersSelection, createCircleForSelectedMarker, theme, calculateFinalOpacity]
    );

    const addEventMarkers = useCallback(
        (events: any[], citySettings?: CitySettings) => {
            if (!events?.length || !map || !mapgl) return { validEventsCount: 0, markersCount: 0 };

            if (citySettings?.is_map_enabled === false) {
                console.log("📍 События ДПС и Чисто отключены в настройках города");
                return { validEventsCount: 0, markersCount: 0 };
            }
            const groupedEvents = groupEventsByLocation(events);
            let validEventsCount = 0;
            let markersCount = 0;

            Object.entries(groupedEvents).forEach(([_, locationEvents]) => {
                const firstEvent = locationEvents[0];

                if (!firstEvent.currentPoint) {
                    console.warn("Событие без currentPoint, пропускаем:", firstEvent);
                    return;
                }

                const coordinates: [number, number] = [
                    parseFloat(firstEvent.currentPoint.longitude.toString()),
                    parseFloat(firstEvent.currentPoint.latitude.toString()),
                ];

                validEventsCount++;
                const { markerType, locationType } = getMarkerTypeByEvents(locationEvents);
                const timestamp = firstEvent.timestamp || Math.floor(Date.now() / 1000);

                const markerId = `event-${coordinates[0]}-${coordinates[1]}-${firstEvent.currentLocation?.type || 0
                    }`;

                try {
                    const initialOpacity = calculateFinalOpacity(markerType, "event", timestamp);

                    const marker = createManagedMarker(mapgl, map, {
                        coordinates,
                        icon: createMarkerIcon(markerType, locationType, theme),
                        size: MARKER_SIZES.normal,
                        anchor: MARKER_ANCHORS.normal,
                        opacity: initialOpacity,
                        userData: {
                            type: "event",
                            markerType,
                            events: locationEvents,
                            coordinates,
                            markerId,
                            location:
                                firstEvent.currentLocation?.name ||
                                firstEvent.location_name ||
                                firstEvent.message ||
                                "Неизвестная локация",
                            locationType: locationType,
                            locationId: firstEvent.currentLocationId,
                            currentOpacity: initialOpacity, // Для плавной анимации
                        },
                    }, markerRenderingMode);

                    marker.on("click", () => {
                        console.log("🖱️ Клик по маркеру:", markerId);

                        YandexMetrika.mapEventIconClick(markerType as "dps" | "clean" | "dtp");

                        centerMapOnMarker(map, coordinates, isMapReady);
                        selectMarker(markerId);

                        onCloseFilters?.();
                        setMarkerModal({
                            isOpen: true,
                            data: {
                                type: "event",
                                title:
                                    firstEvent.currentLocation?.name ||
                                    firstEvent.location_name ||
                                    "Локация",
                                events: locationEvents,
                                eventCount: locationEvents.length,
                            },
                        });

                        HapticFeedback.medium();
                    });

                    const markerIndex = currentMarkers.current.length;
                    currentMarkers.current.push({ marker, timestamp, type: "event" });
                    markersCount++;

                    // Добавляем в индекс
                    const typeKey = markerType as "dps" | "clean" | "dtp";
                    markersByType.current[typeKey].add(markerIndex);
                } catch (error) {
                    console.warn("Ошибка создания маркера события:", error);
                }
            });
            console.log("addEventMarkers вызван:", {
                eventsCount: events?.length,
                citySettings,
                is_map_enabled: citySettings?.is_map_enabled
            });
            return { validEventsCount, markersCount };
        },
        [map, mapgl, theme, isMapReady, selectMarker, calculateFinalOpacity, markerRenderingMode]
    );

    const addWebcamMarkers = useCallback(
        (webcams: any[]) => {
            if (!webcams?.length || !map || !mapgl) return { validWebcamsCount: 0, markersCount: 0 };

            let validWebcamsCount = 0;
            let markersCount = 0;

            webcams.forEach((webcam) => {
                if (
                    !webcam.latitude ||
                    !webcam.longitude ||
                    webcam.latitude === 0 ||
                    webcam.longitude === 0 ||
                    isNaN(webcam.latitude) ||
                    isNaN(webcam.longitude)
                ) {
                    console.warn("Невалидные координаты веб-камеры:", webcam);
                    return;
                }

                validWebcamsCount++;

                const coordinates: [number, number] = [
                    parseFloat(webcam.longitude.toString()),
                    parseFloat(webcam.latitude.toString()),
                ];

                const markerId = `webcam-${webcam.id}`;

                try {
                    const initialOpacity = calculateFinalOpacity("webcam", "webcam");

                    const marker = createManagedMarker(mapgl, map, {
                        coordinates,
                        icon: createMarkerIcon("webcam", 0, theme),
                        size: MARKER_SIZES.normal,
                        anchor: MARKER_ANCHORS.normal,
                        opacity: initialOpacity,
                        userData: {
                            type: "webcam",
                            markerType: "webcam",
                            webcam,
                            coordinates,
                            markerId,
                            name: webcam.name,
                            locationType: 0,
                            currentOpacity: initialOpacity, // Для плавной анимации
                        },
                    }, markerRenderingMode);

                    marker.on("click", () => {
                        console.log("🖱️ Клик по веб-камере:", markerId);

                        YandexMetrika.mapEventIconClick("camera");

                        centerMapOnMarker(map, coordinates, isMapReady);
                        selectMarker(markerId);

                        onCloseFilters?.();
                        setMarkerModal({
                            isOpen: true,
                            data: {
                                type: "webcam",
                                title: webcam.name,
                                webcam: webcam,
                            },
                        });

                        HapticFeedback.light();
                    });

                    const markerIndex = currentMarkers.current.length;
                    currentMarkers.current.push({ marker, type: "webcam" });
                    markersCount++;

                    // Добавляем в индекс
                    markersByType.current.webcam.add(markerIndex);
                } catch (error) {
                    console.warn("Ошибка создания маркера веб-камеры:", error);
                }
            });

            return { validWebcamsCount, markersCount };
        },
        [map, mapgl, theme, isMapReady, selectMarker, calculateFinalOpacity, markerRenderingMode]
    );

    const updateEventMarkersOnly = useCallback(
        (newEvents: any[], citySettings?: CitySettings) => {
            if (!newEvents?.length || !map || !mapgl || !isMapReady) return;
            if (citySettings?.is_map_enabled === false) {
                console.log("📍 WebSocket события отключены - карта неактивна");
                return;
            }
            console.log("🔄 Обновляем только события WebSocket, не трогая существующие маркеры");

            const groupedEvents = groupEventsByLocation(newEvents);
            let addedCount = 0;

            Object.entries(groupedEvents).forEach(([_, locationEvents]) => {
                const firstEvent = locationEvents[0];

                //  ЛОГИКА ДЛЯ WEBSOCKET ФОРМАТА
                let coordinates: [number, number] | null = null;
                let locationName = '';
                let locationType = 0;

                if (firstEvent.locations && firstEvent.locations.length > 0) {
                    // WebSocket формат
                    const location = firstEvent.locations[0];
                    if (location.points && location.points.length > 0) {
                        const point = location.points[0];
                        coordinates = [point.longitude, point.latitude];
                        locationName = location.name;
                        locationType = location.type;
                    }
                } else if (firstEvent.currentPoint) {
                    // API формат
                    coordinates = [
                        parseFloat(firstEvent.currentPoint.longitude.toString()),
                        parseFloat(firstEvent.currentPoint.latitude.toString()),
                    ];
                    locationName = firstEvent.currentLocation?.name || firstEvent.location_name || '';
                    locationType = firstEvent.currentLocation?.type || 0;
                }

                if (!coordinates) {
                    console.warn("Нет координат для события:", firstEvent);
                    return;
                }

                const markerId = `event-${coordinates[0]}-${coordinates[1]}-${locationType}`;

                // Проверяем, есть ли уже такой маркер
                const existingMarker = currentMarkers.current.find(
                    ({ marker }) => (marker.userData as any)?.markerId === markerId
                );

                if (!existingMarker) {
                    // Создаем только новый маркер
                    const { markerType, locationType: eventLocationType } = getMarkerTypeByEvents(locationEvents);
                    const timestamp = firstEvent.timestamp || Math.floor(Date.now() / 1000);

                    try {
                        const marker = createManagedMarker(mapgl, map, {
                            coordinates,
                            icon: createMarkerIcon(markerType, eventLocationType, theme),
                            size: [0, 0],
                            anchor: MARKER_ANCHORS.normal,
                            opacity: 0,
                            userData: {
                                type: "event",
                                markerType,
                                events: locationEvents,
                                coordinates,
                                markerId,
                                location: locationName || "Неизвестная локация",
                                locationType: eventLocationType,
                                currentOpacity: 0, // Для плавной анимации
                            },
                        }, markerRenderingMode);
                        let startTime = performance.now();
                        const targetOpacity = calculateFinalOpacity(markerType, "event", timestamp);

                        const animate = () => {
                            const elapsed = performance.now() - startTime;
                            const progress = Math.min(elapsed / 1200, 1); // 1200ms анимация

                            // Пружинистый эффект
                            const spring = progress < 1
                                ? 1 + 4 * Math.pow(progress - 1, 3) + 3 * Math.pow(progress - 1, 2)
                                : 1;
                            const size: [number, number] = MARKER_SIZES.normal.map(s => s * spring) as [number, number];
                            const opacity = targetOpacity * progress;

                            try {
                                marker.setIcon({
                                    icon: createMarkerIcon(markerType, eventLocationType, theme),
                                    size: size,
                                    anchor: MARKER_ANCHORS.normal,
                                    opacity: opacity,
                                });

                                // Обновляем currentOpacity для плавности следующих анимаций
                                if (marker.userData) {
                                    (marker.userData as any).currentOpacity = opacity;
                                }
                            } catch (error) {
                                console.warn("Ошибка анимации маркера:", error);
                                return;
                            }

                            if (progress < 1) requestAnimationFrame(animate);
                        };
                        requestAnimationFrame(animate);
                        marker.on("click", () => {
                            YandexMetrika.mapEventIconClick(markerType as "dps" | "clean" | "dtp");
                            
                            centerMapOnMarker(map, coordinates!, isMapReady);
                            selectMarker(markerId);
                            onCloseFilters?.();
                            setMarkerModal({
                                isOpen: true,
                                data: {
                                    type: "event",
                                    title: locationName || "Локация",
                                    events: locationEvents,
                                    eventCount: locationEvents.length,
                                },
                            });
                            HapticFeedback.medium();
                        });

                        const markerIndex = currentMarkers.current.length;
                        currentMarkers.current.push({ marker, timestamp, type: "event" });
                        addedCount++;

                        // Добавляем в индекс
                        const typeKey = markerType as "dps" | "clean" | "dtp";
                        markersByType.current[typeKey].add(markerIndex);

                        console.log("✅ Добавлен новый маркер из WebSocket:", markerId, locationName);

                        // Виброотклик при появлении новой метки
                        HapticFeedback.medium();
                    } catch (error) {
                        console.warn("Ошибка создания нового маркера события:", error);
                    }
                } else {
                    console.log("🔄 Маркер уже существует:", markerId);
                }
            });

            console.log(`✅ Добавлено новых маркеров из WebSocket: ${addedCount}`);
        },
        [map, mapgl, theme, isMapReady, selectMarker, calculateFinalOpacity, markerRenderingMode]
    );

    const addMarkers = useCallback(
        (events: any[], webcams: any[], onMapReady?: () => void, forceUpdate: boolean = false, citySettings?: CitySettings) => {

            if (!map || !mapgl || !isMapReady) {
                console.log("❌ Карта не готова для маркеров");
                return;
            }

            if (markersAddedRef.current && !forceUpdate) {
                console.log("❌ Маркеры уже добавлены, пропускаем");
                return;
            }

            clearAllMarkers();

            const hasEventsData = events && events.length > 0;
            const hasWebcamsData = webcams && webcams.length > 0;

            if (!hasEventsData && !hasWebcamsData) {
                console.log("📍 Нет данных для маркеров, это нормально");
                markersAddedRef.current = true;
                if (onMapReady) onMapReady();
                return;
            }

            console.log("📍 Начинаем добавление маркеров:", {
                events: events?.length || 0,
                webcams: webcams?.length || 0,
                currentTheme: theme,
                markerRenderingMode,
            });

            let totalMarkersCount = 0;
            let totalValidEventsCount = 0;
            let totalValidWebcamsCount = 0;

            if (hasWebcamsData) {
                const { validWebcamsCount, markersCount } = addWebcamMarkers(webcams);
                totalValidWebcamsCount = validWebcamsCount;
                totalMarkersCount += markersCount;
            }

            if (hasEventsData) {
                const { validEventsCount, markersCount } = addEventMarkers(events, citySettings);
                totalValidEventsCount = validEventsCount;
                totalMarkersCount += markersCount;
            }

            markersAddedRef.current = true;

            console.log("✅ Обработка маркеров завершена:", {
                markersCount: totalMarkersCount,
                validEventsCount: totalValidEventsCount,
                validWebcamsCount: totalValidWebcamsCount,
                totalDataItems: (events?.length || 0) + (webcams?.length || 0),
                themeUsed: theme,
                markerRenderingMode,
            });

            if (onMapReady) onMapReady();

            if ((totalValidEventsCount > 0 || totalValidWebcamsCount > 0) && totalMarkersCount === 0) {
                console.warn("⚠️ Есть валидные данные, но маркеры не создались");
            }
        },
        [map, mapgl, isMapReady, theme, markerRenderingMode, clearAllMarkers, addEventMarkers, addWebcamMarkers]
    );

    const updateMarkerOpacity = useCallback(() => {
        if (!isMapReady || !currentMarkers.current.length) return;

        // Фильтруем и удаляем устаревшие маркеры (старше 2 часов)
        const indicesToRemove: number[] = [];

        currentMarkers.current = currentMarkers.current.filter(({ marker, timestamp, type }, index) => {
            // Вычисляем прозрачность по времени
            if (type === "event" && typeof timestamp === "number") {
                const timeOpacity = calculateMarkerOpacity(timestamp);

                // Удаляем только если старше 2 часов
                if (timeOpacity <= 0) {
                    indicesToRemove.push(index);
                    try {
                        marker.destroy();
                        return false;
                    } catch (error) {
                        console.warn("Ошибка при удалении маркера:", error);
                        return false;
                    }
                }
            }
            return true;
        });

        // Обновляем индексы после удаления маркеров
        if (indicesToRemove.length > 0) {
            // Пересоздаем индексы
            markersByType.current = {
                dps: new Set(),
                webcam: new Set(),
                clean: new Set(),
                dtp: new Set(),
            };

            currentMarkers.current.forEach(({ marker }, newIndex) => {
                const userData = marker.userData as any;
                if (userData?.markerType) {
                    const type = userData.markerType as "dps" | "webcam" | "clean" | "dtp";
                    markersByType.current[type].add(newIndex);
                }
            });

            console.log(`🗑️ Удалено ${indicesToRemove.length} устаревших маркеров, индекс обновлен`);
        }
    }, [isMapReady]);

    // Обновление прозрачности по времени (раздельно от фильтров)
    useEffect(() => {
        if (!isMapReady) return;

        const updateTimeOpacity = () => {
            currentMarkers.current.forEach(({ marker, timestamp, type }) => {
                if (type === "event" && typeof timestamp === "number") {
                    const userData = marker.userData as any;
                    if (!userData) return;

                    const markerType = userData.markerType;

                    // Проверяем, виден ли маркер по фильтру
                    const isVisibleByFilter = isMarkerVisibleByFilter(markerType, type);

                    // Если скрыт фильтром, не обновляем
                    if (!isVisibleByFilter) return;

                    // Вычисляем финальную прозрачность (фильтр + время)
                    const finalOpacity = calculateFinalOpacity(markerType, type, timestamp);
                    const isSelected = userData.markerId === selectedMarkerId;

                    try {
                        marker.setIcon({
                            icon: createMarkerIcon(markerType, userData.locationType, theme),
                            size: isSelected ? MARKER_SIZES.selected : MARKER_SIZES.normal,
                            anchor: isSelected ? MARKER_ANCHORS.selected : MARKER_ANCHORS.normal,
                            opacity: finalOpacity,
                        });
                        (marker.userData as any).currentOpacity = finalOpacity;
                    } catch (error) {
                        console.warn("Ошибка обновления прозрачности:", error);
                    }
                }
            });
        };

        const interval = setInterval(updateTimeOpacity, 60 * 1000);
        return () => clearInterval(interval);
    }, [isMapReady, theme, selectedMarkerId, isMarkerVisibleByFilter, calculateFinalOpacity]);

    // Добавляем функцию для обновления иконок маркеров при смене темы (с батчингом)
    const updateMarkersTheme = useCallback(() => {
        if (!isMapReady || currentMarkers.current.length === 0) return;

        console.log("🎨 Обновляем тему маркеров:", theme);

        // Собираем все обновления
        const updates = currentMarkers.current.map(({ marker }) => {
            const userData = marker.userData as any;
            if (!userData) return null;

            const isSelected = userData.markerId === selectedMarkerId;
            const currentOpacity = userData.currentOpacity ?? 1;

            return {
                marker,
                iconConfig: {
                    icon: createMarkerIcon(userData.markerType, userData.locationType, theme),
                    size: isSelected ? MARKER_SIZES.selected : MARKER_SIZES.normal,
                    anchor: isSelected ? MARKER_ANCHORS.selected : MARKER_ANCHORS.normal,
                    opacity: currentOpacity,
                }
            };
        }).filter(Boolean);

        // Батчим все обновления в один кадр анимации
        if (updates.length > 0) {
            requestAnimationFrame(() => {
                updates.forEach((update) => {
                    if (!update) return;
                    try {
                        update.marker.setIcon(update.iconConfig);
                    } catch (error) {
                        console.warn("Ошибка при обновлении темы маркера:", error);
                    }
                });
                console.log(`⚡ Обновлена тема для ${updates.length} маркеров батчем`);
            });
        }
    }, [theme, selectedMarkerId, isMapReady]);


    return {
        selectedMarkerId,
        markerModal,
        setMarkerModal,
        setSelectedMarkerId,
        markersAddedRef,
        addMarkers,
        updateEventMarkersOnly,
        clearAllMarkers,
        resetAllMarkersSelection,
        updateMarkerOpacity,
        updateMarkersTheme,
    };
};
